import React, { Fragment } from 'react'
import propTypes from 'prop-types'
import { R } from '../../../../utils/currency'
import { getYearFromAge } from '../../../../utils/dateParser'
import { annuity } from '../../../../utils/financeCalculations'
import Box from '@material-ui/core/Box'

import './styles.sass'
import { Card, CardContent} from '@components/layout/Card'
import { groupBy } from 'lodash'
import { Badge } from '@components/Badge'
import { appColors } from '../../../../globalStyles'

export function Tooltip ({ cashflowEvents, cashflowEventsDrawStatus, onClose, hideCloseButton }) {
  const eventTypeGroupedCashflowEvents = groupBy(cashflowEvents, 'event_type')

  return (
    <Card className={hideCloseButton ? 'mr-0' : 'mr-8'}>
      <CloseButton hidden={hideCloseButton} onClose={onClose}/>
      <CardContent className='p-4 pb-0'> 
        <CashflowEventTooltip
          eventTypeGroupedCashflowEvents={eventTypeGroupedCashflowEvents}
          drawStatus={cashflowEventsDrawStatus}
        />
      </CardContent>
    </Card>
  )
}

const CashflowEventTooltip = ({ eventTypeGroupedCashflowEvents, drawStatus }) => {
  const incomeEvents = eventTypeGroupedCashflowEvents.income || []
  const expenseEvents = eventTypeGroupedCashflowEvents.expense || []

  return (
    <>
      <ExpenseEvents events={expenseEvents} drawStatus={drawStatus} />
      <IncomeEvents events={incomeEvents} />
    </>
  )
}

const IncomeEvents = ({ events }) => {
  if (!events.some((event) => event.status === 'active' ))
    return null

  const year = getYearFromAge(events[0].pointAge, events[0].currentAge)

  return (
    <>
      <h1>Income Events</h1>
      <div className='flex my-2 mb-4'>
        <Badge>{year}</Badge>
      </div>
      {events.map((event, idx) => (
        <Event key={idx} event={event} />
      ))}
    </>
  )
}

const ExpenseEvents = ({ events, drawStatus }) => {
  if (!events.some((event) => event.status === 'active' ))
    return null

  const year = getYearFromAge(events[0].pointAge, events[0].currentAge)
  const { partial, fulfilled, amount } = drawStatus

  return (
    <>
      <h1>Expense Events</h1>
      <div className='flex my-2 mb-4 gap-2'>
        <Badge>{year}</Badge>
        { fulfilled && !partial && <Badge variant='success'>Fulfilled</Badge> }
        { partial && <Badge variant='warning'>Partially fulfilled</Badge> }
        { !fulfilled && <Badge variant='error'>Unfulfilled</Badge> }
      </div>
      {events.map((event, idx) => (
        <Event key={idx} event={event} />
      ))}
      { partial &&
        <div className={`bg-[${appColors.red}] text-white rounded text-sm max-w-[30ch] p-2 mb-4`}>
          {R(amount).format()} could not be fulfilled from the available income and assets at this year.
        </div>
      }
    </>
  )
}

const Event = ({ event }) => {
  const { status } = event
  if (status === 'inactive')
    return null

  return (
    <>
      <Content event={event} />
    </>
  )
}

const Content = ({ event }) => {
  const { pointAge, currentAge, inflation } = event
  return (
    <Fragment>
      <h2>{event.name}</h2>
      { eventIsRecurring(event) && <small>Recurring</small> }
      <Box display='flex' alignItems='stretch' mt='0.25rem' mb='1rem'>
        <PresentValue event={event} pointAge={pointAge} currentAge={currentAge} inflation={inflation} />
        <FutureValue event={event} pointAge={pointAge} currentAge={currentAge} inflation={inflation} />
      </Box>
    </Fragment>
  )
}

export const CloseButton = ({ hidden, onClose }) => !hidden &&
  <button onClick={onClose} className='absolute top-0 right-0 pr-4 text-3xl cursor-pointer'>
    <span>x</span>
  </button>

const PresentValue = ({ event, pointAge, currentAge, inflation }) =>
  <Box mx='0.5rem'>
    <p>Present Value</p>
    <h2 style={{ color: event.event_type === 'income' ? appColors.success : appColors.red }}>
      {R(presentValueOfEvent(event, pointAge, currentAge, inflation)).format()}
    </h2>
  </Box>

const FutureValue = ({ event, pointAge, currentAge, inflation }) =>
  <Box mx='0.5rem'>
    <p>Future Value</p>
    <h2 style={{ color: event.event_type === 'income' ? appColors.success : appColors.red }}>
      {R(futureValueOfEvent(event, pointAge, currentAge, inflation)).format()}
    </h2>
  </Box>

const presentValueOfEvent = (event, pointAge, currentAge, inflation) => {
  if (event.event_type === 'income') {
    const period = inflation > event.annual_inflation
      ? currentAge - pointAge
      : pointAge - currentAge

    const inflationToApply = inflation > event.annual_inflation
      ? inflation - event.annual_inflation
      : event.annual_inflation - inflation

    return annuity(event.amount, period, inflationToApply)
  } else {
    return event.amount
  }
}

const futureValueOfEvent = (event, pointAge, currentAge, inflation) => {
  const period = pointAge - currentAge

  const fv = event.event_type === 'income'
    ? annuity(event.amount, period, event.annual_inflation || 0)
    : annuity(event.amount, period, event.annual_inflation || inflation)

  return fv
}

const eventIsRecurring = (event) =>
  event.frequency > 1

Tooltip.propTypes = {
  cashflowEvents: propTypes.array.isRequired,
  cashflowEventsDrawStatus: propTypes.object.isRequired,
  hideCloseButton: propTypes.bool,
  onClose: propTypes.func
}
